import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, CheckCircle, XCircle, X } from "lucide-react";
import {
  fetchProponenteAdmin,
  aprovarDocumento,
  reprovarDocumento,
} from "../../../services/adminService";
import type { ProponenteAdminDetail, HistoricoDocumento } from "../../../services/adminService";

export default function ProponenteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [proponente, setProponente] = useState<ProponenteAdminDetail | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<"approve" | "reject" | null>(
    null
  );
  const [modalDocId, setModalDocId] = useState<number | null>(null);
  const [modalObservacoes, setModalObservacoes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Preview state
  const [expandedDocId, setExpandedDocId] = useState<number | null>(null);
  const [blobUrls, setBlobUrls] = useState<Record<number, string>>({});
  const [fetchingDocId, setFetchingDocId] = useState<number | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fitFullDocId, setFitFullDocId] = useState<number | null>(null);
  const prevExpandedRef = useRef<number | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<'pendente' | 'aprovado' | 'reprovado' | 'todos'>('pendente');

  // Histórico state
  const [historicoModalOpen, setHistoricoModalOpen] = useState(false);
  const [historicoDocumento, setHistoricoDocumento] = useState<ProponenteAdminDetail['documentos'][0] | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProponenteAdmin(Number(id));
      setProponente(data);
    } catch (err) {
      const e = err as Error;
      setError(e?.message || "Erro carregando proponente");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const docId = expandedDocId;
    const prev = prevExpandedRef.current;
    if (prev != null && prev !== docId && blobUrls[prev]) {
      try {
        URL.revokeObjectURL(blobUrls[prev]);
      } catch {
        // ignore
      }
      setBlobUrls((prevMap) => {
        const copy = { ...prevMap };
        delete copy[prev!];
        return copy;
      });
    }
    prevExpandedRef.current = docId ?? null;

    if (!docId) return;
    const doc = proponente?.documentos.find((x) => x.id === docId);
    if (!doc || !doc.arquivo?.url) return;

    const apiBase = import.meta.env.VITE_API_BASE_URL as string;
    const fileUrl = doc.arquivo.url.startsWith("http")
      ? doc.arquivo.url
      : `${apiBase}${doc.arquivo.url}`;

    if (blobUrls[docId]) return;

    let cancelled = false;
    const fetchBlob = async () => {
      setFetchingDocId(docId);
      setFetchError(null);
      try {
        const resp = await fetch(fileUrl, { credentials: "include" });
        if (!resp.ok) throw new Error(`Erro ao baixar arquivo: ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        setBlobUrls((prev) => ({ ...prev, [docId]: objectUrl }));
      } catch (err) {
        const e = err as Error;
        if (!cancelled) setFetchError(e.message || "Erro ao buscar arquivo");
      } finally {
        if (!cancelled) setFetchingDocId(null);
      }
    };

    fetchBlob();

    return () => {
      cancelled = true;
    };
  }, [expandedDocId, proponente, blobUrls]);

  const openActionModal = (action: "approve" | "reject", docId: number) => {
    setModalAction(action);
    setModalDocId(docId);
    setModalObservacoes("");
    setActionError(null);
    setModalOpen(true);
  };

  const closeActionModal = () => {
    setModalOpen(false);
    setModalAction(null);
    setModalDocId(null);
    setModalObservacoes("");
    setActionError(null);
  };

  const confirmAction = async () => {
    if (!modalAction || modalDocId == null) return;
    setActionLoading(true);
    setActionError(null);
    try {
      if (modalAction === "approve") {
        await aprovarDocumento(modalDocId, modalObservacoes || undefined);
      } else {
        await reprovarDocumento(modalDocId, modalObservacoes || undefined);
      }
      await load();
      closeActionModal();
    } catch (err) {
      const e = err as Error;
      setActionError(e?.message || "Erro ao executar ação");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="bg-white border border-gray-200 p-8 text-center">
          <div className="text-gray-600">Carregando...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (!proponente) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="bg-white border border-gray-200 p-8 text-center">
          <div className="text-gray-600">Proponente não encontrado</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Voltar</span>
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">
          {proponente.nome_completo}
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Análise de cadastro e documentação
        </p>
      </div>

      {/* Informações do Proponente */}
      <div className="bg-white border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">
            Informações Pessoais
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-medium text-gray-600">Email</label>
              <p className="text-sm text-gray-900 mt-1">{proponente.email}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">CPF</label>
              <p className="text-sm text-gray-900 mt-1">{proponente.cpf}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Documentos */}
      <div className="bg-white border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">
            DOCUMENTOS ENVIADOS
          </h2>
        </div>
        <div className="p-6">
          {proponente.documentos.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              Nenhum documento enviado
            </div>
          ) : (
            <>
              {/* Abas de filtro */}
              <div className="mb-6 border-b border-gray-200">
                <nav className="flex space-x-8">
                  {[
                    { id: 'pendente' as const, label: 'Pendentes', count: proponente.documentos.filter(d => d.status === 'pendente').length },
                    { id: 'aprovado' as const, label: 'Aprovados', count: proponente.documentos.filter(d => d.status === 'aprovado').length },
                    { id: 'reprovado' as const, label: 'Recusados', count: proponente.documentos.filter(d => d.status === 'reprovado').length },
                    { id: 'todos' as const, label: 'Todos', count: proponente.documentos.length },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`${
                        activeTab === tab.id
                          ? 'border-indigo-500 text-indigo-600' 
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                    >
                      {tab.label}
                      {tab.count > 0 && (
                        <span className={`ml-2 ${
                          activeTab === tab.id ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'
                        } py-0.5 px-2 rounded-full text-xs font-medium`}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Documentos filtrados por aba */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {proponente.documentos
                  .filter(d => activeTab === 'todos' || d.status === activeTab)
                  .map((d) => {
                  return (
                    <div
                      key={d.id}
                      className="bg-indigo-900 text-white rounded-lg overflow-hidden shadow-lg"
                    >
                      {/* Card Header */}
                      <div className="p-6 flex flex-col items-center">
                        <h3 className="font-semibold text-base text-center mb-8 min-h-[3rem] flex items-center">
                          {d.documento_tipo}
                        </h3>

                        {/* Icon */}
                        <div className="mb-4">
                          <FileText className="w-16 h-16 text-white opacity-80" />
                        </div>

                        {/* Visualizar Button */}
                        <button
                          onClick={() => {
                            if (expandedDocId === d.id) {
                              setExpandedDocId(null);
                              setFitFullDocId(null);
                            } else {
                              setExpandedDocId(d.id);
                              setFitFullDocId(d.id);
                            }
                          }}
                          className="text-white text-sm mb-6 hover:underline"
                        >
                          Visualizar
                        </button>
                      </div>

                      {/* Card Footer */}
                      <div className="bg-indigo-950 bg-opacity-50 p-4 space-y-2">
                        <div className="text-xs">
                          <span className="opacity-70">Enviado em: </span>
                          <span className="font-medium">
                            {d.created_at
                              ? new Date(d.created_at).toLocaleDateString(
                                  "pt-BR"
                                )
                              : "-"}
                          </span>
                        </div>

                        <div className="text-xs flex items-center space-x-2">
                          <span className="opacity-70">Status:</span>
                          <span className="font-medium capitalize flex items-center space-x-1">
                            <span>{d.status}</span>
                            {d.status === "aprovado" && (
                              <CheckCircle className="w-3 h-3 text-green-400" />
                            )}
                            {d.status === "reprovado" && (
                              <XCircle className="w-3 h-3 text-red-400" />
                            )}
                          </span>
                        </div>
                        {(d.aprovado_por || d.aprovado_em) && (
                          <div className="text-xs mt-1">
                            <span className="opacity-70">
                              {d.status === "aprovado"
                                ? "Aprovado por"
                                : d.status === "reprovado"
                                ? "Reprovado por"
                                : "Validado por"}
                            </span>
                            <span className="font-medium ml-1">
                              {d.aprovado_por ?? "-"}
                            </span>
                            {d.aprovado_em && (
                              <span className="ml-2 text-xs text-gray-200">
                                {new Date(d.aprovado_em).toLocaleString(
                                  "pt-BR"
                                )}
                              </span>
                            )}
                          </div>
                        )}

                        {d.observacoes && (
                          <div className="text-xs">
                            <span className="opacity-70">Obs: </span>
                            <span className="font-medium">{d.observacoes}</span>
                          </div>
                        )}

                        {/* Action Buttons - apenas para pendentes */}
                        {d.status === "pendente" && (
                          <div className="flex items-center space-x-2 pt-2">
                            <button
                              onClick={() => openActionModal("reject", d.id)}
                              disabled={actionLoading && modalDocId === d.id}
                              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-3 text-xs font-medium disabled:opacity-50 rounded"
                            >
                              Recusar
                            </button>
                            <button
                              onClick={() => openActionModal("approve", d.id)}
                              disabled={actionLoading && modalDocId === d.id}
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-3 text-xs font-medium disabled:opacity-50 rounded"
                            >
                              Validar
                            </button>
                          </div>
                        )}

                        {/* Mensagens de status para aprovados e reprovados */}
                        {d.status === "aprovado" && (
                          <div className="text-xs text-center pt-2 text-green-300">
                            ✓ Documento validado
                          </div>
                        )}
                        {d.status === "reprovado" && (
                          <div className="text-xs text-center pt-2 text-red-300">
                            ✗ Aguardando reenvio
                          </div>
                        )}

                        {/* Botão Ver Histórico - se houver múltiplos envios */}
                        {d.tem_historico && (
                          <button
                            onClick={() => {
                              setHistoricoDocumento(d);
                              setHistoricoModalOpen(true);
                            }}
                            className="w-full mt-2 text-xs text-white opacity-70 hover:opacity-100 underline"
                          >
                            Ver histórico ({d.total_envios} envios)
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Preview expandido - aparece abaixo do grid */}
              {expandedDocId &&
                (() => {
                  const d = proponente.documentos.find(
                    (doc) => doc.id === expandedDocId
                  );
                  if (!d || !d.arquivo?.url) return null;

                  const blobUrl = blobUrls[d.id];
                  const originalUrl = d.arquivo?.url ?? null;
                  const apiBase =
                    (import.meta.env.VITE_API_BASE_URL as string) ||
                    "http://localhost:3000";
                  const absoluteOriginalUrl = originalUrl
                    ? originalUrl.startsWith("http")
                      ? originalUrl
                      : `${apiBase}${originalUrl}`
                    : null;
                  const previewUrl = blobUrl ?? absoluteOriginalUrl;
                  const isPdf =
                    !!d.arquivo?.filename &&
                    d.arquivo.filename.toLowerCase().endsWith(".pdf");

                  return (
                    <div className="mt-6 border border-gray-300 bg-white rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center space-x-3">
                          <FileText className="w-4 h-4 text-gray-500" />
                          <div className="text-sm font-medium text-gray-800">
                            {d.arquivo?.filename}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <a
                            href={absoluteOriginalUrl ?? undefined}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-blue-600 hover:underline px-2 py-1 border border-blue-100 rounded"
                          >
                            Abrir em nova aba
                          </a>
                          <a
                            href={absoluteOriginalUrl ?? undefined}
                            download
                            className="text-sm text-gray-700 hover:underline px-2 py-1 border border-gray-100 rounded"
                          >
                            Baixar
                          </a>
                          <button
                            onClick={() =>
                              setFitFullDocId(
                                fitFullDocId === d.id ? null : d.id
                              )
                            }
                            className="text-sm px-2 py-1 border border-gray-100 rounded bg-white hover:bg-gray-50"
                          >
                            {fitFullDocId === d.id ? "Reduzir" : "Expandir"}
                          </button>
                          <button
                            onClick={() => setExpandedDocId(null)}
                            className="text-sm px-2 py-1 border border-gray-100 rounded bg-white hover:bg-gray-50"
                          >
                            Fechar
                          </button>
                        </div>
                      </div>
                      {fetchingDocId === d.id ? (
                        <div className="p-4 text-sm text-gray-600">
                          Carregando arquivo...
                        </div>
                      ) : fetchError ? (
                        <div className="p-4 text-sm text-red-600">
                          Erro ao carregar arquivo: {fetchError}.{" "}
                          <a
                            href={originalUrl ?? undefined}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 underline"
                          >
                            Abrir em nova aba
                          </a>
                        </div>
                      ) : isPdf ? (
                        <object
                          data={previewUrl!}
                          type="application/pdf"
                          className={`w-full ${
                            fitFullDocId === d.id ? "h-[80vh]" : "h-96"
                          }`}
                          aria-label={d.arquivo.filename}
                        >
                          <div className="p-4">
                            <p className="text-sm text-gray-700">
                              Não foi possível exibir o PDF no navegador.
                            </p>
                            <a
                              href={originalUrl ?? undefined}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 underline"
                            >
                              Abrir/baixar arquivo em nova aba
                            </a>
                          </div>
                        </object>
                      ) : (
                        <img
                          src={previewUrl ?? ""}
                          alt={d.arquivo.filename}
                          className="w-full object-contain"
                        />
                      )}
                    </div>
                  );
                })()}
            </>
          )}
        </div>
      </div>

      {/* Modal de Histórico */}
      {historicoModalOpen && historicoDocumento && (
        <div 
          className="fixed inset-0 flex items-center justify-center p-4" 
          style={{ 
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
          onClick={() => setHistoricoModalOpen(false)}
        >
            <div 
              className="bg-white max-w-3xl w-full border border-gray-300 rounded-lg max-h-[80vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-base font-semibold text-gray-900">
                Histórico: {historicoDocumento.documento_tipo}
              </h3>
              <button
                onClick={() => setHistoricoModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {historicoDocumento.historico?.map((h: HistoricoDocumento, index: number) => (
                  <div
                    key={h.id}
                    className={`border rounded-lg p-4 ${
                      index === 0 ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {index === 0 && (
                          <span className="px-2 py-1 bg-indigo-500 text-white text-xs rounded-full font-medium">
                            Atual
                          </span>
                        )}
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                          h.status === 'aprovado' ? 'bg-green-100 text-green-800' :
                          h.status === 'reprovado' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {h.status}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        Enviado em {new Date(h.created_at).toLocaleString('pt-BR')}
                      </span>
                    </div>
                    
                    {h.arquivo && (
                      <div className="mb-2">
                        <a
                          href={h.arquivo.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-blue-600 hover:underline flex items-center space-x-1"
                        >
                          <FileText className="w-4 h-4" />
                          <span>{h.arquivo.filename}</span>
                        </a>
                      </div>
                    )}

                    {(h.aprovado_por || h.aprovado_em) && (
                      <div className="text-sm text-gray-600 mb-1">
                        <strong>
                          {h.status === 'aprovado' ? 'Aprovado' : h.status === 'reprovado' ? 'Reprovado' : 'Validado'}
                        </strong> por <strong>{h.aprovado_por || '-'}</strong>
                        {h.aprovado_em && (
                          <span className="ml-1">
                            em {new Date(h.aprovado_em).toLocaleString('pt-BR')}
                          </span>
                        )}
                      </div>
                    )}

                    {h.observacoes && (
                      <div className="mt-2 p-2 bg-gray-100 rounded text-sm text-gray-700">
                        <strong>Observações:</strong> {h.observacoes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setHistoricoModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 text-sm font-medium rounded"
                >
                  Fechar
                </button>
              </div>
            </div>
        </div>
      )}

      {/* Modal de Ação */}
      {modalOpen && (
        <div 
          className="fixed inset-0 flex items-center justify-center p-4" 
          style={{ 
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
          onClick={closeActionModal}
        >
            <div 
              className="bg-white max-w-lg w-full border border-gray-300 rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">
                {modalAction === "approve"
                  ? "Aprovar Documento"
                  : "Reprovar Documento"}
              </h3>
              <button
                onClick={closeActionModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Observações (opcional)
              </label>
              <textarea
                value={modalObservacoes}
                onChange={(e) => setModalObservacoes(e.target.value)}
                className="w-full border border-gray-300 p-3 text-sm h-32 focus:outline-none focus:border-blue-500 rounded"
                placeholder="Adicione observações sobre a decisão..."
              />
              {actionError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
                  {actionError}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={closeActionModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium rounded"
              >
                Cancelar
              </button>
              <button
                onClick={confirmAction}
                disabled={actionLoading}
                className={`px-4 py-2 text-white text-sm font-medium disabled:opacity-50 rounded ${
                  modalAction === "approve"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {actionLoading ? "Processando..." : "Confirmar"}
              </button>
              </div>
            </div>
        </div>
      )}
    </div>
  );
}
